import { ArrowLeftIcon, ArrowRightIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router';
import type { AreaContext } from '../../components/layout/AppShell';
import { AdvancedFields } from '../../components/wizard/AdvancedFields';
import { GenerationOverlay } from '../../components/wizard/GenerationOverlay';
import { OutlineBuilder } from '../../components/wizard/OutlineBuilder';
import { PropertyField } from '../../components/wizard/PropertyField';
import { ReferenceSourcedPreview } from '../../components/wizard/ReferenceSourcedPreview';
import { ServiceSourcedPreview } from '../../components/wizard/ServiceSourcedPreview';
import { SlideCollectionPicker } from '../../components/wizard/SlideCollectionPicker';
import { useToast } from '../../components/ui/toast';
import { displayValue } from '../../data/displayValue';
import { deleteDraft } from '../../data/drafts';
import { fieldLabel } from '../../data/fieldLabel';
import { ApiError, describeApiError } from '../../data/http';
import { generateDocument } from '../../data/wizard';
import { parseOutline } from '../../data/outline';
import { parseSlideIds } from '../../data/slideCollectionValue';
import { isWizardStep, useWizard, type WizardStep } from '../../hooks/useWizard';
import type { FormField, GenerationResult } from '../../data/types';

function ValueWithDetail({ text, detail }: { text: string; detail: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;

    const dismiss = (event: PointerEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [open]);

  return (
    <span ref={wrapper} className="relative inline-flex items-center gap-1">
      {text}
      <button
        type="button"
        aria-expanded={open}
        aria-label={t('wizard.review.showDetail')}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpen(false);
        }}
        className="rounded text-muted-foreground transition hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        <InformationCircleIcon className="size-4" aria-hidden="true" />
      </button>

      {open ? (
        <span
          role="tooltip"
          className="absolute left-0 top-full z-20 mt-1 w-max max-w-xs rounded-sm border border-border bg-surface px-3 py-2 text-xs text-muted-foreground shadow-lg"
        >
          {detail}
        </span>
      ) : null}
    </span>
  );
}

/** An Image property's `source` and, if pinned to one collection item, that item's id. */
function imageSourceConfig(configJson: string): { source: string; collectionItem: string } {
  try {
    const parsed = JSON.parse(configJson || '{}') as { source?: unknown; collectionItem?: unknown };
    return {
      source: typeof parsed.source === 'string' ? parsed.source : 'upload',
      collectionItem: typeof parsed.collectionItem === 'string' ? parsed.collectionItem : ''
    };
  } catch {
    return { source: 'upload', collectionItem: '' };
  }
}

/** The `columns` a Table property was configured with, in review-only (read-only) form. */
function tableColumns(configJson: string): string[] {
  try {
    const parsed = JSON.parse(configJson || '{}') as { columns?: unknown };
    return Array.isArray(parsed.columns) ? parsed.columns.map(String) : [];
  } catch {
    return [];
  }
}

function tableRows(value: string): Array<Record<string, string>> {
  if (!value.trim()) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? (parsed as Array<Record<string, string>>) : [];
  } catch {
    return [];
  }
}

function TableValue({ configJson, value }: { configJson: string; value: string }) {
  const { t } = useTranslation();
  const columns = tableColumns(configJson);
  const rows = tableRows(value);

  if (columns.length === 0 || rows.length === 0) {
    return <span className="text-muted-foreground">{t('table.grid.empty')}</span>;
  }

  return (
    <div className="mt-1 overflow-auto rounded border border-border">
      <table className="min-w-full divide-y divide-border text-sm">
        <thead className="bg-muted">
          <tr>
            {columns.map((column) => (
              <th key={column} scope="col" className="px-3 py-1.5 text-left font-semibold text-foreground">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row, index) => (
            <tr key={index}>
              {columns.map((column) => (
                <td key={column} className="px-3 py-1.5 text-foreground">
                  {row[column] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReviewValue({
  field,
  label,
  value,
  templateId,
  values
}: {
  field: FormField;
  label: string;
  value: string;
  templateId?: string;
  values: Record<string, string>;
}) {
  const { t } = useTranslation();
  const propertyType = field.propertyType;

  if (propertyType === 'SlideCollection') {
    const count = parseSlideIds(value).length;
    return t('wizard.review.slideCount', { count });
  }

  if (propertyType === 'List') {
    const count = parseOutline(value).length;
    return t('wizard.review.outlineCount', { count });
  }

  if (propertyType === 'Table') {
    return <TableValue configJson={field.configJson} value={value} />;
  }

  if (propertyType === 'Image') {
    const trimmed = value.trim();

    // An upload travels as the image itself, so there is nothing to look up.
    if (trimmed.startsWith('data:')) {
      return /^data:image\//i.test(trimmed) ? (
        <img src={trimmed} alt={label} className="mt-1 size-24 rounded border border-border object-cover" />
      ) : (
        t('wizard.review.fileValue')
      );
    }

    const { source, collectionItem: pinnedItem } = imageSourceConfig(field.configJson);
    // An "open" collection has no pinned item — the picked value itself is the item id.
    const collectionItem = source === 'collection' ? pinnedItem || trimmed : undefined;

    if (source === 'collection' && !collectionItem) {
      return <span className="text-muted-foreground">{t('wizard.review.empty')}</span>;
    }

    if (source === 'service') {
      return (
        <ServiceSourcedPreview
          templateId={templateId}
          tokenName={field.tokenName}
          propertyType="Image"
          configJson={field.configJson}
          displayName={label}
          values={values}
          resolvedDefault={field.resolvedDefault}
          collectionItem={collectionItem}
        />
      );
    }

    if (templateId && field.resolvedDefault) {
      return (
        <ReferenceSourcedPreview
          templateId={templateId}
          propertyType="Image"
          displayName={label}
          entryKey={field.resolvedDefault}
          collectionItem={collectionItem}
        />
      );
    }

    return <span className="text-muted-foreground">{t('wizard.review.empty')}</span>;
  }

  const display = displayValue(propertyType, value);

  switch (display.kind) {
    case 'text':
      return display.detail ? <ValueWithDetail text={display.text} detail={display.detail} /> : display.text;
    case 'yesNo':
      return display.yes ? t('common.yes') : t('common.no');
    case 'file':
      return display.image ? (
        <img src={display.image} alt={label} className="mt-1 size-24 rounded border border-border object-cover" />
      ) : (
        t('wizard.review.fileValue')
      );
    default:
      return <span className="text-muted-foreground">{t('wizard.review.empty')}</span>;
  }
}

/** A long-text or tabular value needs the full row width; anything else can share a row with its neighbour. */
function isWideReviewField(propertyType: string): boolean {
  return propertyType === 'MultilineText' || propertyType === 'Table';
}

/** Pairs fields two-per-row in source order for a scannable grid; a wide field gets its own full row. */
function reviewRows(fields: FormField[]): FormField[][] {
  const rows: FormField[][] = [];
  let pair: FormField[] = [];

  for (const field of fields) {
    if (isWideReviewField(field.propertyType)) {
      if (pair.length > 0) rows.push(pair);
      pair = [];
      rows.push([field]);
      continue;
    }

    pair.push(field);
    if (pair.length === 2) {
      rows.push(pair);
      pair = [];
    }
  }

  if (pair.length > 0) rows.push(pair);
  return rows;
}

export function WizardPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { templateId, step } = useParams();
  const [searchParams] = useSearchParams();
  const { roles } = useOutletContext<AreaContext>();

  const wizard = useWizard(templateId, searchParams.get('draft'));
  const [showErrors, setShowErrors] = useState(false);
  const [running, setRunning] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  // Null until the user decides; their choice then outranks the automatic opening.
  const [advancedOpen, setAdvancedOpen] = useState<boolean | null>(null);
  const [result, setResult] = useState<GenerationResult | null>(null);

  if (!isWizardStep(step)) {
    // Keeps `?draft=` so a draft saved under a step that no longer exists still resumes.
    return <Navigate to={{ pathname: `/app/new/${templateId}/details`, search: searchParams.toString() }} replace />;
  }

  const index = wizard.steps.indexOf(step);
  const missing = wizard.missingRequired(step);
  const invalidLength = wizard.invalidLength(step);
  const blocking = [...missing, ...invalidLength];
  const plain = wizard.visibleFields(step);
  const advanced = wizard.advancedFields();
  const missingAdvanced = blocking.filter((field) => field.isAdvanced);

  // Derived from the defaults, never from live input: keying this on the current
  // values would collapse the section under the user the moment they finished
  // typing the very field it was opened for.
  const unfilledByDefault = advanced.some((field) => field.isRequired && !field.resolvedDefault.trim());
  const showAdvanced = advancedOpen ?? unfilledByDefault;

  const goTo = (next: WizardStep) => {
    setShowErrors(false);
    void navigate(`/app/new/${templateId}/${next}`);
  };

  const next = () => {
    // DC-10, client half: the server validates again during the dry run.
    if (blocking.length > 0) {
      setShowErrors(true);
      if (missingAdvanced.length > 0) setAdvancedOpen(true);
      return;
    }

    goTo(wizard.steps[index + 1]);
  };

  const saveDraftNow = async () => {
    if (savingDraft) return;
    setSavingDraft(true);
    try {
      await wizard.persist(step);
      toast({ title: t('drafts.saved'), type: 'success' });
    } catch (err) {
      toast({ title: describeApiError(err, t('errors.saveDraft')), type: 'error' });
    } finally {
      setSavingDraft(false);
    }
  };

  const generate = async () => {
    const outstanding = [...wizard.missingRequired(), ...wizard.invalidLength()];
    if (outstanding.length > 0) {
      setShowErrors(true);
      toast({ title: t('wizard.form.fixRequired'), type: 'error' });
      return;
    }

    setRunning(true);
    setResult(null);

    // Stopped at the click, so the server's own pipeline time is not counted against the user.
    const elapsed = wizard.elapsedSeconds();

    try {
      // Validate first (DC-02) so a failure costs nothing and reports real phases.
      const validation = await generateDocument(templateId!, wizard.values, true, elapsed);
      setResult(validation);

      if (!validation.valid) {
        setRunning(false);
        return;
      }

      const generated = await generateDocument(templateId!, wizard.values, false, elapsed);
      setResult(generated);
      setRunning(false);
    } catch (err) {
      setRunning(false);

      // The API returns 400 for a failed dry run/generation, but the body is still a
      // GenerationResult — route it through the overlay so property-level errors show,
      // instead of collapsing it into a generic "Request failed with 400" toast.
      if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'propertyResults' in err.body) {
        setResult(err.body as GenerationResult);
        return;
      }

      toast({ title: describeApiError(err, t('errors.generate')), type: 'error' });
    }
  };

  const primaryButton =
    'inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground ring-1 ring-primary-hover transition hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-60';
  const secondaryButton =
    'inline-flex items-center justify-center gap-2 rounded border border-input bg-surface px-3 py-2 text-sm font-semibold text-foreground transition hover:bg-muted';

  // Shared by the top and bottom bars so both stay in sync.
  const actionButtons = () => (
    <>
      {index > 0 ? (
        <button type="button" onClick={() => goTo(wizard.steps[index - 1])} className={secondaryButton}>
          <ArrowLeftIcon className="size-4" aria-hidden="true" />
          {t('common.back')}
        </button>
      ) : null}

      {step === 'review' ? (
        <button type="button" onClick={() => void generate()} disabled={running} className={primaryButton}>
          {t('wizard.generate.action')}
        </button>
      ) : (
        <button type="button" onClick={next} className={primaryButton}>
          {t('common.next')}
          <ArrowRightIcon className="size-4" aria-hidden="true" />
        </button>
      )}

      <button type="button" onClick={() => void saveDraftNow()} disabled={savingDraft} className={secondaryButton}>
        {savingDraft ? t('common.saving') : t('drafts.save')}
      </button>
    </>
  );

  const formReady = !wizard.loading && !wizard.error && wizard.form;

  return (
    <div>
      <Link
        to="/app/new"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground transition hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        <ArrowLeftIcon className="size-4" aria-hidden="true" />
        {t('wizard.picker.title')}
      </Link>

      <h1 className="mt-3 text-3xl font-heading font-semibold tracking-normal text-foreground">
        {wizard.form?.name ?? t('wizard.title')}
      </h1>

      <div className="sticky top-16 z-20 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface/95 px-4 py-3 backdrop-blur">
        <ol className="flex flex-wrap gap-2" aria-label={t('wizard.stepsLabel')}>
          {wizard.steps.map((name, position) => (
            <li key={name}>
              <span
                aria-current={name === step ? 'step' : undefined}
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${
                  name === step ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}
              >
                <span className="font-mono">{position + 1}</span>
                {t(`wizard.steps.${name}`)}
              </span>
            </li>
          ))}
        </ol>

        {formReady ? <div className="flex flex-wrap items-center justify-end gap-3">{actionButtons()}</div> : null}
      </div>

      {wizard.loading ? (
        <p className="mt-6 text-sm text-muted-foreground" aria-live="polite">
          {t('common.loading')}
        </p>
      ) : null}

      {wizard.error ? (
        <p role="alert" className="mt-6 border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
          {wizard.error}
        </p>
      ) : null}

      {formReady ? (
        <>
          {step === 'review' ? (
              <section className="mt-6 rounded-lg border border-border bg-surface p-6">
                <h2 className="text-base font-semibold text-foreground">{t('wizard.review.title')}</h2>
                <dl className="mt-2 divide-y divide-border">
                  {reviewRows(wizard.visibleFields('review')).map((row) => (
                    <div key={row[0].tokenName} className={`grid gap-x-6 gap-y-3 py-3 ${row.length === 2 ? 'sm:grid-cols-2' : ''}`}>
                      {row.map((field) => (
                        <div key={field.tokenName}>
                          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {fieldLabel(field.displayName)}
                          </dt>
                          <dd className="mt-0.5 break-words text-sm text-foreground">
                            <ReviewValue
                              field={field}
                              label={fieldLabel(field.displayName)}
                              value={wizard.values[field.tokenName] ?? ''}
                              templateId={templateId}
                              values={wizard.values}
                            />
                          </dd>
                        </div>
                      ))}
                    </div>
                  ))}
                </dl>
              </section>
            ) : step === 'slides' ? (
              <section className="mt-6 rounded-lg border border-border bg-surface p-6">
                {plain.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('wizard.form.noFields')}</p>
                ) : (
                  <div className="space-y-8">
                    {plain.map((field) => (
                      <div key={field.tokenName}>
                        <span className="text-sm font-medium text-foreground">
                          {fieldLabel(field.displayName)}
                          {field.isRequired ? <span className="ml-1 text-destructive">*</span> : null}
                        </span>
                        <SlideCollectionPicker
                          value={wizard.values[field.tokenName] ?? ''}
                          onChange={(value) => wizard.setValue(field.tokenName, value)}
                          configJson={field.configJson}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ) : step === 'outline' ? (
              <section className="mt-6 rounded-lg border border-border bg-surface p-6">
                {plain.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('wizard.form.noFields')}</p>
                ) : (
                  <div className="space-y-8">
                    {plain.map((field) => (
                      <div key={field.tokenName}>
                        <span className="text-sm font-medium text-foreground">
                          {fieldLabel(field.displayName)}
                          {field.isRequired ? <span className="ml-1 text-destructive">*</span> : null}
                        </span>
                        <OutlineBuilder
                          value={wizard.values[field.tokenName] ?? ''}
                          onChange={(value) => wizard.setValue(field.tokenName, value)}
                          // The outline field's own (possibly large) JSON value is never useful
                          // grounding context for its own chat.
                          context={Object.fromEntries(
                            Object.entries(wizard.values).filter(([token]) => token !== field.tokenName)
                          )}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ) : (
              <>
                {plain.length > 0 || advanced.length === 0 ? (
                  <section className="mt-6 rounded-lg border border-border bg-surface p-6">
                    {plain.length === 0 ? (
                      <p className="text-sm text-muted-foreground">{t('wizard.form.noFields')}</p>
                    ) : (
                      // Dense flow backfills the cell a full-width field leaves empty above it.
                      <div className="grid grid-flow-row-dense gap-4 sm:grid-cols-2">
                        {plain.map((field) => (
                          <PropertyField
                            key={field.tokenName}
                            field={field}
                            value={wizard.values[field.tokenName] ?? ''}
                            invalid={showErrors && blocking.some((m) => m.tokenName === field.tokenName)}
                            onChange={(value) => wizard.setValue(field.tokenName, value)}
                            values={wizard.values}
                            templateId={templateId}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                ) : null}

                <AdvancedFields
                  fields={advanced}
                  values={wizard.values}
                  invalidTokens={showErrors ? missingAdvanced.map((field) => field.tokenName) : []}
                  needsAttention={missingAdvanced.length > 0}
                  open={showAdvanced}
                  onToggle={setAdvancedOpen}
                  onChange={wizard.setValue}
                  templateId={templateId}
                />
              </>
            )}

          <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
            {showErrors && blocking.length > 0 ? (
              <p role="alert" className="mr-auto text-sm text-destructive">
                {t('wizard.form.fixRequired')}
              </p>
            ) : null}

            {actionButtons()}
          </div>
        </>
      ) : null}

      {running || result ? (
        <GenerationOverlay
          running={running}
          result={result}
          showFullLog={roles.includes('admin')}
          onDelivered={() => {
            if (!wizard.draftId) return;
            // The document is out, so a leftover draft must not surface as a failure.
            void deleteDraft(wizard.draftId).catch(() => undefined);
          }}
          onClose={() => {
            const documentId = result?.documentId;
            setResult(null);
            void navigate(documentId ? '/app/new' : `/app/new/${templateId}/review`);
          }}
        />
      ) : null}
    </div>
  );
}
